import React, { useEffect, useMemo, useState } from 'react'
import { Container } from '../../components'
import {
    Button,
    Divider,
    InputAdornment,
    TextField,
    Tooltip,
    useTheme,
} from '@material-ui/core'
import { createStyles, makeStyles } from '@material-ui/core/styles'
import { Routes as HelperRoutes, axiosInstance } from '../../helpers'
import { Routes } from '../../navigation/config/routes'
import {
    FeaturedHomeVendor,
    useNVLFeaturedHome,
} from '../../main/containers/national-vendor-list'
import Image from '../../assets/NVL_Pic.jpg'
import ArrowForwardIcon from '@material-ui/icons/ArrowForward'
import AssignmentTurnedInOutlinedIcon from '@material-ui/icons/AssignmentTurnedInOutlined'
import LocationOnOutlinedIcon from '@material-ui/icons/LocationOnOutlined'
import { ReactComponent as RibbonAirplaneDarkIcon } from '../../assets/icons/fluent_ribbon-dark.svg'
import { ReactComponent as RibbonThumbGreenIcon } from '../../assets/icons/ribbon-thumb_green.svg'
import { ReactComponent as FeaturedVendorRibbon } from '../../assets/Featured_Vendor_Badge.svg'
import EzosLogo from '../../assets/ezos-logo-primary-dark.png'
import {
    PlanStatus,
    Service,
    ServiceCategoryCode,
    getServiceImageDetails,
    isNationalCatalogService,
} from '../../models'
import { useService, useUser } from '../../hooks'
import { Autocomplete } from '@material-ui/lab'
import SearchIcon from '@material-ui/icons/Search'
import StarsIcon from '@material-ui/icons/Stars'
import { useHistory } from 'react-router-dom'
import { EZVendorModal } from './EZVendorModal'
import LoadingGIF from '../../assets/Loading_Animation_2.gif'
import SkillCatLogoSmall from '../../assets/SkillCatLogo_small.png'
import { useStripe } from '../../hooks/useStripe'
import EZVendorLogo1 from '../../assets/EZVendorLogo1.png'

interface ServiceWOCount {
    count: number
    id: number
    name: string
    service_category?: ServiceCategoryCode | null
    apartment?: number | null
}

const useVendorActionButtonStyles = makeStyles((theme) =>
    createStyles({
        searchBtn: {
            backgroundColor: '#008C85',
            color: 'white',
            textTransform: 'none',
            fontWeight: 700,
            fontSize: 16,
            borderRadius: 10,
            padding: theme.spacing(1.5, 3),
            minHeight: 56,
            boxShadow: 'none',
            transition:
                'transform 0.18s ease, box-shadow 0.18s ease, background-color 0.18s ease',
            '&:hover': {
                backgroundColor: '#00756F',
                transform: 'translateY(-2px) scale(1.02)',
                boxShadow: '0 8px 20px rgba(0, 140, 133, 0.35)',
            },
        },
        recommendedBtn: {
            color: theme.palette.darkGreen.main,
            borderColor: theme.palette.darkGreen.main,
            borderWidth: 2,
            textTransform: 'none',
            fontWeight: 700,
            fontSize: 16,
            whiteSpace: 'nowrap',
            borderRadius: 10,
            padding: theme.spacing(1.5, 3),
            minHeight: 56,
            backgroundColor: 'white',
            transition:
                'transform 0.18s ease, box-shadow 0.18s ease, background-color 0.18s ease, border-color 0.18s ease',
            '&:hover': {
                backgroundColor: 'rgba(0, 140, 133, 0.08)',
                borderWidth: 2,
                borderColor: theme.palette.darkGreen.main,
                transform: 'translateY(-2px) scale(1.02)',
                boxShadow: '0 8px 20px rgba(0, 140, 133, 0.2)',
            },
        },
    }),
)

interface VendorNameOption {
    id: number
    name: string
    workspace_count?: number
}

const formatVendorNameOption = (vendor: VendorNameOption): string => {
    const count = vendor.workspace_count ?? 0
    const label = count === 1 ? 'property' : 'properties'
    return `${vendor.name} (${count} ${label})`
}

const EZVENDOR = 'EZVENDOR'

/** Shared width so both search fields stay the same size when centered. */
const SEARCH_FIELD_WIDTH = 560

const buildListPath = (options?: {
    serviceId?: number
    q?: string
    vendorId?: number
}) => {
    const params = new URLSearchParams()
    if (options?.serviceId != null) {
        params.set('serviceId', String(options.serviceId))
    }
    if (options?.vendorId != null) {
        params.set('vendorId', String(options.vendorId))
    } else if (options?.q) {
        params.set('q', options.q)
    }
    const query = params.toString()
    return query ? `${Routes.public.nvl.list}?${query}` : Routes.public.nvl.list
}

export const NVLHomePage = () => {
    const { workspaceUser, rootUser } = useUser()

    const [vendorNames, setVendorNames] = useState<VendorNameOption[]>([])
    const [extrasLoading, setExtrasLoading] = useState(true)

    const [mostUsedServices, setMostUsedServices] = useState<ServiceWOCount[]>(
        [],
    )

    const [selectedService, setSelectedService] = useState<
        Service | undefined
    >()
    const [openVendorDropDown, setOpenVendorDropDown] = useState(false)
    const [vendorSearchText, setVendorSearchText] = useState('')

    const [hasEZVendor, setHasEZVendor] = useState(false)
    const [openEZVendorModal, setOpenEZVendorModal] = useState(false)

    const theme = useTheme()
    const history = useHistory()
    const actionBtnClasses = useVendorActionButtonStyles()

    const { serviceList, getServiceList } = useService()
    const nationalServiceList = useMemo(
        () => serviceList.filter(isNationalCatalogService),
        [serviceList],
    )

    const {
        createStripeCheckoutSession,
        createStripePortalSession,
    } = useStripe()

    const {
        vendors: featuredVendors,
        isLoading: featuredLoading,
    } = useNVLFeaturedHome()

    const loading = featuredLoading || extrasLoading

    useEffect(() => {
        let cancelled = false

        Promise.all([
            axiosInstance.get(`workorder/get_wo_service_counts/`),
            axiosInstance.get<VendorNameOption[]>(
                `company/national_vendor_list/vendor_names/`,
            ),
        ])
            .then((values) => {
                if (cancelled) {
                    return
                }
                setMostUsedServices(values[0].data ?? [])
                setVendorNames(values[1].data ?? [])
            })
            .finally(() => {
                if (!cancelled) {
                    setExtrasLoading(false)
                }
            })

        // Non-blocking: service dropdown options + EZVendor gate
        getServiceList({}).catch(() => undefined)

        if (workspaceUser?.active_workspace.has_infrastructure) {
            setHasEZVendor(true)
        } else if (rootUser) {
            axiosInstance
                .get(`company/stripe_sessions/get_plan_status/`)
                .then((res) => {
                    if (!cancelled && res.data === EZVENDOR) {
                        setHasEZVendor(true)
                    }
                })
                .catch(() => undefined)
        }

        return () => {
            cancelled = true
        }
    }, [])

    const ensureNVLAccess = () => {
        if (hasEZVendor) {
            return true
        }
        if (rootUser) {
            setOpenEZVendorModal(true)
            return false
        }
        history.push(HelperRoutes.login)
        return false
    }

    const goToVendorList = (options?: {
        serviceId?: number
        q?: string
        vendorId?: number
    }) => {
        if (!ensureNVLAccess()) {
            return
        }
        setOpenVendorDropDown(false)
        history.push(buildListPath(options))
    }

    const handleServiceSelect = (service?: Service | null) => {
        setSelectedService(service ?? undefined)
        if (service) {
            goToVendorList({ serviceId: service.id })
        }
    }

    const handleServiceTileClick = (serviceId: number) => {
        const matched = nationalServiceList.find(
            (service) => service.id === serviceId,
        )
        if (matched) {
            setSelectedService(matched)
        }
        goToVendorList({ serviceId })
    }

    const handleVendorSearch = () => {
        const query = vendorSearchText.trim()
        if (!query) {
            goToVendorList()
            return
        }
        goToVendorList({ q: query })
    }

    const matchingVendors = vendorNames
        .filter((vendor) =>
            vendor.name
                .toLocaleLowerCase()
                .includes(vendorSearchText.toLocaleLowerCase()),
        )
        .slice(0, 20)

    const CreateStripeInteraction = (yearly: boolean = false) => {
        if (hasEZVendor) {
            const body = {
                return_url: window.location.href,
            }
            createStripePortalSession(body)
        } else {
            const body = {
                plan_type: PlanStatus.EZVENDOR,
                success_url: window.location.href,
                yearly: yearly,
            }
            createStripeCheckoutSession(body)
        }
    }

    const giantFieldStyles = {
        backgroundColor: '#ffffff',
        borderRadius: 12,
        boxShadow: '0 8px 28px rgba(0, 80, 75, 0.12)',
        border: '2px solid #008C85',
    }

    return (
        <Container
            flex={1}
            direction="column"
            style={{
                overflow: 'auto',
                minWidth: '700px',
                padding: theme.spacing(1, 3, 6, 3),
            }}
            onClick={() => {
                setOpenVendorDropDown(false)
            }}
        >
            {(!!rootUser &&
                !workspaceUser?.active_workspace.has_infrastructure) ||
            !rootUser ? (
                <Container
                    justifyContent="flex-end"
                    alignItems="center"
                    style={{
                        width: '100%',
                        marginBottom: theme.spacing(0.75),
                        gap: theme.spacing(1),
                    }}
                >
                    {!rootUser ? (
                        <>
                            <Button
                                style={{
                                    color: theme.palette.primary.dark,
                                    textTransform: 'none',
                                    fontWeight: 700,
                                }}
                                onClick={() => history.push(HelperRoutes.login)}
                            >
                                Log-in
                            </Button>
                            <Button
                                style={{
                                    color: 'white',
                                    backgroundColor: theme.palette.primary.dark,
                                    textTransform: 'none',
                                    fontWeight: 700,
                                }}
                                onClick={() =>
                                    history.push(HelperRoutes.register)
                                }
                            >
                                Get Started
                                <ArrowForwardIcon />
                            </Button>
                        </>
                    ) : (
                        <Button
                            variant="contained"
                            style={{
                                backgroundColor: theme.palette.darkBlue.main,
                                color: 'white',
                                textTransform: 'none',
                                cursor: 'pointer',
                            }}
                            onClick={() => {
                                if (hasEZVendor) {
                                    CreateStripeInteraction()
                                } else {
                                    setOpenEZVendorModal(true)
                                }
                            }}
                        >
                            {hasEZVendor
                                ? 'Update Subscription'
                                : 'Upgrade To EZVendor'}
                        </Button>
                    )}
                </Container>
            ) : null}

            <Container
                direction="column"
                style={{
                    position: 'relative',
                    minHeight: 160,
                    width: '100%',
                    minWidth: '850px',
                    backgroundImage: `url(${Image})`,
                    backgroundSize: 'cover',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center 78%',
                    borderRadius: 16,
                    overflow: 'hidden',
                    justifyContent: 'center',
                    alignItems: 'center',
                    marginBottom: theme.spacing(2),
                }}
            >
                <div
                    style={{
                        position: 'absolute',
                        inset: 0,
                        background:
                            'linear-gradient(180deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.35) 55%, rgba(0,0,0,0.5) 100%)',
                        pointerEvents: 'none',
                    }}
                />
                <Container
                    direction="column"
                    alignItems="center"
                    style={{
                        position: 'relative',
                        zIndex: 1,
                        padding: theme.spacing(1.5, 3),
                        textAlign: 'center',
                    }}
                >
                    <Container
                        style={{
                            fontWeight: 800,
                            fontSize: 40,
                            lineHeight: '44px',
                            color: 'white',
                            textShadow:
                                '0 1px 2px rgba(0,0,0,0.7), 0 2px 12px rgba(0,0,0,0.55)',
                            letterSpacing: 0.2,
                            marginBottom: theme.spacing(1),
                        }}
                    >
                        National Vendor List
                    </Container>
                    <Container
                        style={{
                            fontWeight: 600,
                            fontSize: 17,
                            color: 'white',
                            textShadow:
                                '0 1px 2px rgba(0,0,0,0.7), 0 2px 10px rgba(0,0,0,0.5)',
                        }}
                    >
                        {`Here to make your search a little  EZ'ier`}
                    </Container>
                    <Container
                        style={{
                            fontWeight: 700,
                            fontSize: 22,
                            color: 'white',
                            paddingTop: theme.spacing(0.75),
                            textShadow:
                                '0 1px 2px rgba(0,0,0,0.7), 0 2px 10px rgba(0,0,0,0.5)',
                        }}
                        flexWrap="wrap"
                    >
                        Find Certified and top-rated vendors in your area
                    </Container>
                </Container>
            </Container>

            {loading ? (
                <Container
                    flex={1}
                    alignItems="center"
                    justifyContent="center"
                    style={{ minHeight: 320 }}
                >
                    <img src={LoadingGIF} alt="Loading" />
                </Container>
            ) : (
                <>
                    {/* Find by Service */}
                    <Container
                        direction="column"
                        style={{
                            width: '100%',
                            marginBottom: theme.spacing(2),
                        }}
                    >
                        <Container
                            alignItems="center"
                            style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr auto 1fr',
                                width: '100%',
                                minHeight: 72,
                                marginBottom: theme.spacing(0.5),
                                columnGap: theme.spacing(2),
                            }}
                        >
                            <Container direction="column">
                                <Container
                                    style={{
                                        fontWeight: 700,
                                        fontSize: 28,
                                        color: '#102A12',
                                        lineHeight: '32px',
                                    }}
                                >
                                    Find by Service
                                </Container>
                                <Container
                                    style={{
                                        fontSize: 14,
                                        color: theme.palette.grey[600],
                                        marginTop: theme.spacing(0.25),
                                    }}
                                >
                                    Search for a trade.
                                </Container>
                            </Container>

                            <Autocomplete
                                id="nvl-home-service-select"
                                options={nationalServiceList}
                                getOptionLabel={(option) => option.name}
                                getOptionSelected={(option, value) =>
                                    option.id === value.id
                                }
                                value={selectedService ?? null}
                                onChange={(_, value) =>
                                    handleServiceSelect(value)
                                }
                                onOpen={() => setOpenVendorDropDown(false)}
                                style={{ width: SEARCH_FIELD_WIDTH }}
                                size="medium"
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="outlined"
                                        placeholder="Search services — e.g. Paint, Clean, Maintenance"
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <>
                                                    <InputAdornment position="start">
                                                        <SearchIcon
                                                            style={{
                                                                color:
                                                                    '#008C85',
                                                                fontSize: 28,
                                                                marginLeft: theme.spacing(
                                                                    1,
                                                                ),
                                                            }}
                                                        />
                                                    </InputAdornment>
                                                    {
                                                        params.InputProps
                                                            .startAdornment
                                                    }
                                                </>
                                            ),
                                            style: {
                                                fontSize: 20,
                                                fontWeight: 500,
                                                paddingTop: 6,
                                                paddingBottom: 6,
                                            },
                                        }}
                                        style={giantFieldStyles}
                                    />
                                )}
                            />

                            <div />
                        </Container>

                        <Container
                            style={{
                                marginTop: theme.spacing(1.5),
                                width: '100%',
                                overflowX: 'auto',
                                overflowY: 'hidden',
                                flexWrap: 'nowrap',
                            }}
                        >
                            {mostUsedServices
                                .filter((row) => row.apartment == null)
                                .map((serviceWoCount) => (
                                    <ServiceBox
                                        key={`MOST_USED_SERVICE_${serviceWoCount.id}`}
                                        serviceWoCount={serviceWoCount}
                                        onClick={() =>
                                            handleServiceTileClick(
                                                serviceWoCount.id,
                                            )
                                        }
                                    />
                                ))}
                        </Container>
                    </Container>

                    {/* Find by Vendor */}
                    <Container direction="column" style={{ width: '100%' }}>
                        <Container
                            alignItems="center"
                            style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr auto 1fr',
                                width: '100%',
                                minHeight: 72,
                                marginBottom: theme.spacing(0.5),
                                columnGap: theme.spacing(2),
                            }}
                        >
                            <Container
                                alignItems="center"
                                style={{
                                    minWidth: 0,
                                    gap: theme.spacing(1.5),
                                    justifyContent: 'space-between',
                                }}
                            >
                                <Container direction="column">
                                    <Container
                                        style={{
                                            fontWeight: 700,
                                            fontSize: 28,
                                            color: '#102A12',
                                            lineHeight: '32px',
                                        }}
                                    >
                                        Find by Vendor
                                    </Container>
                                    <Container
                                        style={{
                                            fontSize: 14,
                                            color: theme.palette.grey[600],
                                            marginTop: theme.spacing(0.25),
                                        }}
                                    >
                                        Search vendors by name.
                                    </Container>
                                </Container>
                                <img
                                    src={EZVendorLogo1}
                                    height={22}
                                    width={150}
                                    alt="EZVendor"
                                    style={{
                                        display: 'block',
                                        flexShrink: 0,
                                    }}
                                />
                            </Container>

                            <Container
                                alignItems="center"
                                style={{
                                    ...giantFieldStyles,
                                    width: SEARCH_FIELD_WIDTH,
                                    position: 'relative',
                                    overflow: 'visible',
                                }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <TextField
                                    variant="standard"
                                    placeholder="Search vendors by name"
                                    value={vendorSearchText}
                                    onChange={(e) => {
                                        setVendorSearchText(e.target.value)
                                        setOpenVendorDropDown(true)
                                    }}
                                    onFocus={() => setOpenVendorDropDown(true)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            handleVendorSearch()
                                        }
                                    }}
                                    InputProps={{
                                        disableUnderline: true,
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon
                                                    style={{
                                                        color: '#008C85',
                                                        fontSize: 28,
                                                        marginLeft: theme.spacing(
                                                            1.5,
                                                        ),
                                                    }}
                                                />
                                            </InputAdornment>
                                        ),
                                    }}
                                    style={{
                                        flex: 1,
                                        padding: theme.spacing(1.5, 1),
                                    }}
                                    inputProps={{
                                        style: {
                                            fontSize: 20,
                                            fontWeight: 500,
                                        },
                                    }}
                                />
                                {openVendorDropDown && vendorSearchText.trim() && (
                                    <Container
                                        direction="column"
                                        style={{
                                            position: 'absolute',
                                            top: '100%',
                                            left: 0,
                                            width: '100%',
                                            maxHeight: 260,
                                            overflow: 'auto',
                                            backgroundColor: 'white',
                                            zIndex: 1000,
                                            borderRadius: 8,
                                            marginTop: 6,
                                            boxShadow:
                                                '0px 4px 12px rgba(0,0,0,0.12)',
                                        }}
                                    >
                                        {matchingVendors.length === 0 ? (
                                            <Container
                                                style={{
                                                    padding: theme.spacing(1.5),
                                                    color:
                                                        theme.palette.grey[600],
                                                    fontSize: 14,
                                                }}
                                            >
                                                No vendors found
                                            </Container>
                                        ) : (
                                            matchingVendors.map((vendor) => (
                                                <Button
                                                    key={vendor.id}
                                                    style={{
                                                        justifyContent:
                                                            'flex-start',
                                                        textTransform: 'none',
                                                        padding: theme.spacing(
                                                            1.5,
                                                        ),
                                                        color:
                                                            theme.palette.text
                                                                .primary,
                                                        fontSize: 16,
                                                    }}
                                                    onClick={() => {
                                                        setVendorSearchText(
                                                            vendor.name,
                                                        )
                                                        goToVendorList({
                                                            vendorId: vendor.id,
                                                        })
                                                    }}
                                                >
                                                    {formatVendorNameOption(
                                                        vendor,
                                                    )}
                                                </Button>
                                            ))
                                        )}
                                    </Container>
                                )}
                            </Container>

                            <Container
                                alignItems="center"
                                style={{ gap: theme.spacing(1.5) }}
                            >
                                <Button
                                    variant="contained"
                                    disableElevation
                                    className={actionBtnClasses.searchBtn}
                                    onClick={handleVendorSearch}
                                >
                                    Search
                                </Button>
                                <Button
                                    variant="outlined"
                                    startIcon={<StarsIcon />}
                                    className={actionBtnClasses.recommendedBtn}
                                    onClick={() => goToVendorList()}
                                >
                                    View Recommended
                                </Button>
                            </Container>
                        </Container>

                        <Container
                            style={{
                                margin: theme.spacing(1.5, 0, 1, 0),
                                paddingTop: theme.spacing(1),
                                width: '100%',
                                overflowX: 'auto',
                                overflowY: 'hidden',
                                flexWrap: 'nowrap',
                            }}
                        >
                            {featuredVendors.map((vendor) => (
                                <FeaturedVendor
                                    key={`FEATURED_VENDOR_${vendor.id}`}
                                    featuredVendor={vendor}
                                    onClick={() => goToVendorList()}
                                />
                            ))}
                        </Container>
                    </Container>
                </>
            )}

            <EZVendorModal
                open={openEZVendorModal}
                handleClose={() => setOpenEZVendorModal(false)}
                createStripeInteraction={CreateStripeInteraction}
            />
        </Container>
    )
}

interface FeaturedVendorProps {
    featuredVendor: FeaturedHomeVendor
    onClick: () => void
}

const formatFeaturedWoCount = (count: number) => {
    if (count >= 1000) {
        return `${Math.floor(count / 1000)}K+`
    }
    if (count >= 100) {
        return `${Math.floor(count / 100) * 100}+`
    }
    return String(count)
}

const FeaturedVendor = (props: FeaturedVendorProps) => {
    const { featuredVendor, onClick } = props

    const theme = useTheme()

    const image = featuredVendor.featured_image
        ? featuredVendor.featured_image
        : Image

    const cityState = [featuredVendor.city, featuredVendor.state]
        .filter((part) => part && part !== '-')
        .join(', ')

    const completeCount = featuredVendor.complete_count ?? 0

    return (
        <Container
            style={{
                position: 'relative',
                width: '255px',
                minWidth: '255px',
                flexShrink: 0,
                border: `1px solid ${theme.palette.lightGrey.main}`,
                borderRadius: '5px',
                marginRight: theme.spacing(3),
                cursor: 'pointer',
                overflow: 'visible',
            }}
            direction="column"
            onClick={onClick}
        >
            <FeaturedVendorRibbon
                style={{
                    position: 'absolute',
                    top: -2,
                    left: -2,
                    zIndex: 3,
                    width: 72,
                    height: 'auto',
                }}
            />
            <img
                style={{
                    height: '130px',
                    width: '254px',
                    borderTopRightRadius: '5px',
                    borderTopLeftRadius: '5px',
                    zIndex: 2,
                    objectFit: 'cover',
                }}
                src={image}
                onError={(event) => {
                    event.currentTarget.src = Image
                }}
            />
            <Container
                direction="column"
                style={{
                    padding: theme.spacing(1, 1.25, 0.75, 1.25),
                    gap: theme.spacing(0.5),
                }}
            >
                <Container
                    style={{
                        fontWeight: 600,
                        fontSize: 16,
                        lineHeight: '20px',
                        color: '#102A12',
                    }}
                >
                    {featuredVendor.name}
                </Container>
                {cityState && (
                    <Container
                        alignItems="center"
                        style={{
                            gap: theme.spacing(0.25),
                            fontWeight: 500,
                            fontSize: 13,
                            color: theme.palette.grey[600],
                            lineHeight: '18px',
                        }}
                    >
                        <LocationOnOutlinedIcon
                            style={{
                                fontSize: 16,
                                color: theme.palette.darkGreen.main,
                            }}
                        />
                        {cityState}
                    </Container>
                )}
            </Container>
            <Divider />
            <Container
                alignItems="center"
                style={{
                    margin: theme.spacing(0.75, 1.25),
                    gap: theme.spacing(1),
                    minHeight: 28,
                }}
            >
                {featuredVendor.is_trained && (
                    <Tooltip title="EZ trained">
                        <span style={{ display: 'flex' }}>
                            <RibbonThumbGreenIcon />
                        </span>
                    </Tooltip>
                )}

                {featuredVendor.will_travel && (
                    <Tooltip title="Will travel">
                        <span style={{ display: 'flex' }}>
                            <RibbonAirplaneDarkIcon />
                        </span>
                    </Tooltip>
                )}

                {featuredVendor.is_skillcat && (
                    <Tooltip title="SkillCat">
                        <img src={SkillCatLogoSmall} alt="Skill Cat Logo" />
                    </Tooltip>
                )}

                <Tooltip title="Work Orders Completed">
                    <Container
                        alignItems="center"
                        style={{
                            gap: theme.spacing(0.5),
                            marginLeft: 'auto',
                        }}
                    >
                        <AssignmentTurnedInOutlinedIcon
                            style={{
                                fontSize: 18,
                                color: theme.palette.darkGreen.main,
                            }}
                        />
                        <Container
                            style={{
                                fontWeight: 600,
                                fontSize: 13,
                                color: '#102A12',
                                lineHeight: '16px',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {formatFeaturedWoCount(completeCount)}
                        </Container>
                    </Container>
                </Tooltip>
            </Container>
        </Container>
    )
}

interface ServiceBoxProps {
    serviceWoCount: ServiceWOCount
    onClick: () => void
}

const formatServiceWoCount = (count: number) => {
    if (count < 1000) {
        return String(count)
    }
    return `${Math.round(count / 1000)}K`
}

const ServiceBox = (props: ServiceBoxProps) => {
    const { serviceWoCount, onClick } = props

    const theme = useTheme()

    const serviceVisual = getServiceImageDetails(
        serviceWoCount.id,
        serviceWoCount.service_category,
    )
    const serviceColor = serviceVisual.color || theme.palette.darkGreen.main

    return (
        <Container
            style={{
                height: '260px',
                width: '200px',
                minWidth: '200px',
                flexShrink: 0,
                border: `1px solid ${theme.palette.lightGrey.main}`,
                borderRadius: '5px',
                marginRight: theme.spacing(3),
                marginTop: theme.spacing(2),
                cursor: 'pointer',
                overflow: 'hidden',
            }}
            direction="column"
            onClick={onClick}
        >
            <img
                style={{
                    height: '190px',
                    width: '200px',
                    objectFit: 'cover',
                    display: 'block',
                }}
                src={serviceVisual.src ?? undefined}
                alt={serviceWoCount.name}
            />

            <Container
                alignItems="center"
                style={{
                    flex: 1,
                    position: 'relative',
                    backgroundColor: '#fff',
                    backgroundImage: `linear-gradient(${serviceColor}33, ${serviceColor}33)`,
                    padding: theme.spacing(1, 1.25),
                    minHeight: 70,
                    gap: theme.spacing(0.75),
                }}
            >
                {serviceVisual.icon && (
                    <Container
                        alignItems="center"
                        justifyContent="center"
                        style={{
                            height: 28,
                            width: 28,
                            borderRadius: 6,
                            backgroundColor: '#fff',
                            flexShrink: 0,
                        }}
                    >
                        <img
                            src={serviceVisual.icon}
                            alt=""
                            style={{
                                height: 18,
                                width: 18,
                                objectFit: 'contain',
                            }}
                        />
                    </Container>
                )}
                <Container
                    style={{
                        fontWeight: 600,
                        fontSize: 16,
                        color: '#102A12',
                        lineHeight: '20px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        minWidth: 0,
                        paddingRight: theme.spacing(4),
                    }}
                >
                    {serviceWoCount.name}
                </Container>
                <Container
                    style={{
                        position: 'absolute',
                        right: theme.spacing(1.25),
                        bottom: theme.spacing(0.75),
                        fontWeight: 600,
                        fontSize: 13,
                        color: serviceColor,
                        lineHeight: '16px',
                    }}
                >
                    {formatServiceWoCount(serviceWoCount.count)}
                </Container>
            </Container>
        </Container>
    )
}
